import { sortDate, sortString, sortNumber } from './sort';
import { stripHtml } from './stripHtml';
import { getColumnValue } from './getColumnValue';
import { translate } from '../Components/Translate';
import get from 'lodash/get';
import ConvertTableToCsvOptions from '../models/utils/ConvertTableToCsvOptions';
import GetChildColumnsOptions from '../models/utils/GetChildColumnsOptions';

export class CsvHelper {
    static convertTableToCsv(options: ConvertTableToCsvOptions) {
        const {
            config,
            columnConfig,
            shareClasses,
            locale,
            translationStore,
            headerDisclaimers,
            footerDisclaimers,
            dataStore,
            explorerDownload,
        } = options;

        const { superscriptSeparator } = explorerDownload;

        const fundCurrencyProperty = config.fundCurrencyProperty;
        const defaultNoDataPlaceholder = config.noDataPlaceholder;
        const workingShareClasses = [...(shareClasses || [])];
        const { sort, columns } = columnConfig;

        const headings: Array<any> = [];

        const workingFooterDisclaimers = this.escapeDisclaimers(
            footerDisclaimers,
            superscriptSeparator
        );

        const workingHeaderDisclaimers = this.escapeDisclaimers(
            headerDisclaimers,
            superscriptSeparator
        );

        if (sort) {
            if (sort.type === 'date') {
                workingShareClasses.sort((a, b) =>
                    sortDate(a[sort.key], b[sort.key])
                );
            }

            if (sort.type === 'string') {
                workingShareClasses.sort((a, b) =>
                    sortString(a[sort.key], b[sort.key])
                );
            }

            if (sort.type === 'number') {
                workingShareClasses.sort((a, b) =>
                    sortNumber(a[sort.key], b[sort.key])
                );
            }

            if (sort.direction === 'DESC') {
                workingShareClasses.reverse();
            }
        }

        const csv = workingShareClasses.map((shareClass, shareClassIndex) => {
            const fundCurrency = this.getFundCurrency(
                shareClass,
                fundCurrencyProperty
            );
            const columnValues: Array<string> = [];
            columns.forEach(column => {
                if (column.type === 'dropdown') {
                    for (const group of column.groups) {
                        this.getChildColumns({
                            dataStore,
                            shareClassIndex,
                            translationStore,
                            headings,
                            columnValues,
                            config,
                            shareClass,
                            locale,
                            fundCurrency,
                            defaultNoDataPlaceholder,
                            column: group,
                            childColumns: group.columns,
                        });
                    }
                } else if (column.type === 'group') {
                    this.getChildColumns({
                        dataStore,
                        column,
                        shareClassIndex,
                        translationStore,
                        headings,
                        columnValues,
                        config,
                        shareClass,
                        locale,
                        fundCurrency,
                        defaultNoDataPlaceholder,
                        childColumns: column.columns,
                    });
                } else if (!column.type) {
                    if (shareClassIndex === 0) {
                        headings.push(
                            translate(translationStore, column.label)
                        );
                    }
                    columnValues.push(
                        getColumnValue({
                            config,
                            column,
                            shareClass,
                            locale,
                            fundCurrency,
                            defaultNoDataPlaceholder,
                            translationStore,
                            dataStore,
                        })
                    );
                }
            });
            return columnValues.map(this.escapeValue).join(',');
        });
        csv.unshift(headings.map(this.escapeValue).join(','));
        if (workingHeaderDisclaimers && workingHeaderDisclaimers.length > 0) {
            csv.unshift(workingHeaderDisclaimers);
        }
        csv.push(workingFooterDisclaimers);
        return csv.join('\n');
    }

    static getChildColumns(options: GetChildColumnsOptions) {
        const {
            dataStore,
            column,
            childColumns,
            shareClassIndex,
            translationStore,
            headings,
            columnValues,
            config,
            shareClass,
            locale,
            fundCurrency,
            defaultNoDataPlaceholder,
        } = options;
        for (const childColumn of childColumns) {
            if (shareClassIndex === 0) {
                const childColumnHeadingParts: Array<string> = [
                    translate(translationStore, column.label),
                    translate(translationStore, childColumn.label),
                ];
                headings.push(childColumnHeadingParts.join(' - '));
            }
            columnValues.push(
                getColumnValue({
                    dataStore,
                    config,
                    shareClass,
                    locale,
                    fundCurrency,
                    defaultNoDataPlaceholder,
                    translationStore,
                    column: childColumn,
                })
            );
        }
    }

    static escapeValue(value) {
        let response = value;
        if (response && response.includes && response.includes('"')) {
            response = response.split('"').join('""');
        }
        return `"${response}"`;
    }

    static escapeDisclaimers(
        disclaimers?: Array<any>,
        superscriptSeparator: string = ' - '
    ) {
        return (disclaimers || [])
            .map(d => {
                if (typeof d === 'string') {
                    return d;
                }
                if (typeof d === 'object' && d.content) {
                    if (d.character) {
                        return `${d.character}${superscriptSeparator}${d.content}`;
                    }
                    return d.content;
                }
            })
            .map(d => `"${stripHtml(d)}"`)
            .join('\n');
    }

    static getFundCurrency(shareClass, fundCurrencyProperty) {
        return get(
            shareClass,
            `properties_pub.${fundCurrencyProperty || 'ccy'}.value`
        );
    }
}



// WEBPACK FOOTER //
// ./src/utils/CsvHelper.ts